import app from 'app';

// In-flight GET /versions promises, keyed by folderId (or storyId when the
// folder is not yet known). Kept out of Vuex state on purpose - promises are
// not serializable and must not be reactive.
const inFlight = {};

function keyFor(folderId, storyId) {
  return folderId || storyId;
}

export default {
  namespaced: true,

  state() {
    return {
      // Version groups keyed by folderId.
      groups: {},
      // Reverse index storyId -> folderId, rebuilt on every setGroup.
      storyToFolder: {},
      // Editor floating Version History panel visibility.
      panelOpen: false,
    };
  },

  getters: {
    groupByFolderId: (state) => (folderId) => state.groups[folderId] || null,

    groupByStoryId: (state) => (storyId) => {
      const folderId = state.storyToFolder[storyId];
      return folderId ? state.groups[folderId] || null : null;
    },

    // Version numbers may have gaps (deletes/branches) - never derive the label
    // from an index. Root has versionNumber null and is displayed as v1.
    versionLabel: () => (version) => {
      if (!version) {
        return '';
      }
      if (version.root || version.versionNumber == null) {
        return 'v1';
      }
      return 'v' + version.versionNumber;
    },

    panelOpen: (state) => state.panelOpen,

    // A version is blocked when someone OTHER than the current user holds its
    // lock (same rule as a locked draft). The current user's own lock is fine.
    isLockedByOther: (state, getters, rootState, rootGetters) => (version) => {
      if (!version || version.lockedByUserId == null) {
        return false;
      }
      const me = rootGetters['user/id'];
      return String(version.lockedByUserId) !== String(me);
    },
  },

  mutations: {
    setGroup(state, payload) {
      const folderId = payload.folderId;

      // Drop stale story->folder mappings for a group we are replacing.
      const previous = state.groups[folderId];
      if (previous) {
        previous.versions.forEach((v) => {
          if (state.storyToFolder[v.storyId] === folderId) {
            app.Vue.delete(state.storyToFolder, v.storyId);
          }
        });
      }

      app.Vue.set(state.groups, folderId, {
        folderId,
        activeStoryId: payload.activeStoryId,
        createdDate: payload.createdDate,
        lastModifiedDate: payload.lastModifiedDate,
        versions: payload.versions || [],
      });

      (payload.versions || []).forEach((v) => {
        app.Vue.set(state.storyToFolder, v.storyId, folderId);
      });
    },

    // Optimistically drop a single version from a cached group so the grid
    // reflows immediately on delete (the server reshape is reconciled by a
    // follow-up REFRESH_AFTER_DELETE). Restored by a force refetch if the
    // delete turns out to have failed.
    removeVersionFromGroup(state, { folderId, storyId }) {
      const group = folderId ? state.groups[folderId] : null;
      if (!group) {
        return;
      }
      group.versions = group.versions.filter((v) => v.storyId !== storyId);
      if (state.storyToFolder[storyId] === folderId) {
        app.Vue.delete(state.storyToFolder, storyId);
      }
    },

    removeGroup(state, folderId) {
      const group = state.groups[folderId];
      if (group) {
        group.versions.forEach((v) => {
          if (state.storyToFolder[v.storyId] === folderId) {
            app.Vue.delete(state.storyToFolder, v.storyId);
          }
        });
        app.Vue.delete(state.groups, folderId);
      }
    },

    setPanelOpen(state, value) {
      state.panelOpen = !!value;
    },
  },

  // @param context {{state, commit, dispatch, getters}}
  // @param payload
  actions: {
    FETCH_VERSIONS({ state, commit }, payload) {
      const storyId = payload.storyId;
      const folderId = payload.folderId || null;
      const force = payload.force || false;
      const key = keyFor(folderId, storyId);

      if (folderId && state.groups[folderId] && !force) {
        return Promise.resolve(state.groups[folderId]);
      }

      const runFetch = () => {
        const promise = app.api.draftStories
          .getVersions(storyId)
          .then((body) => {
            commit('setGroup', body);
            if (inFlight[key] === promise) {
              delete inFlight[key];
            }
            return state.groups[body.folderId];
          })
          .catch((err) => {
            if (inFlight[key] === promise) {
              delete inFlight[key];
            }
            if (err && err.errorCode === 404 && folderId) {
              commit('removeGroup', folderId);
            }
            return Promise.reject(err);
          });
        inFlight[key] = promise;
        return promise;
      };

      if (inFlight[key]) {
        // A non-force call dedupes onto the in-flight request. A force call must
        // NOT piggyback on a request that began BEFORE the mutation it needs to
        // observe (it would resolve to stale versions) - wait for it to settle,
        // then run a fresh fetch.
        if (!force) {
          return inFlight[key];
        }
        return inFlight[key].then(runFetch, runFetch);
      }

      return runFetch();
    },

    BRANCH_VERSION({ state, dispatch, commit }, { storyId, folderId }) {
      return app.api.draftStories.branch(storyId).then((res) => {
        const refetches = [];

        // The old group's root always survives (branching a root is a 400).
        const oldGroup = state.groups[folderId];
        let oldRoot = null;
        if (oldGroup) {
          oldRoot = oldGroup.versions.find((v) => v.root);
        }
        if (oldRoot) {
          refetches.push(
            dispatch('FETCH_VERSIONS', {
              storyId: oldRoot.storyId,
              folderId,
              force: true,
            }).then((refreshed) => {
              // Branching the only child collapses the old group to just its
              // root - dissolve it (same as REFRESH_AFTER_DELETE).
              if (!refreshed || refreshed.versions.length <= 1) {
                commit('removeGroup', folderId);
              }
              return refreshed;
            })
          );
        }

        // The branched story: either the root of a new group, or (no
        // descendants) a plain standalone draft.
        refetches.push(
          dispatch('FETCH_VERSIONS', {
            storyId: res.rootStoryId,
            folderId: res.newFolderId || null,
            force: true,
          }).catch(() => {
            // A story that became standalone has no group - clean up any
            // stale mapping so consumers stop treating it as grouped.
            return null;
          })
        );

        return Promise.all(refetches).then(() => res);
      });
    },

    REFRESH_AFTER_DELETE({ state, dispatch, commit }, payload) {
      const deletedStoryId = payload.deletedStoryId;
      const folderId = payload.folderId;
      const group = state.groups[folderId];

      // Pick a survivor to resolve the (possibly reshaped) group. A group
      // always had >= 2 members before this delete, so a survivor exists.
      let survivor = null;
      if (group) {
        const notDeleted = (v) => v.storyId !== deletedStoryId;
        survivor =
          group.versions.find((v) => v.root && notDeleted(v)) ||
          group.versions.find(notDeleted);
      }

      if (!survivor) {
        commit('removeGroup', folderId);
        return Promise.resolve(null);
      }

      return dispatch('FETCH_VERSIONS', {
        storyId: survivor.storyId,
        folderId,
        force: true,
      }).then(
        (refreshed) => {
          // Group dissolved down to a single standalone draft.
          if (!refreshed || refreshed.versions.length <= 1) {
            commit('removeGroup', folderId);
          }
          return refreshed;
        },
        () => {
          commit('removeGroup', folderId);
          return null;
        }
      );
    },
  },
};
